import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowDownUp,
  Bot,
  CalendarDays,
  Flame,
  Gauge,
  Map,
  RefreshCw,
  SunMedium,
  Wind,
} from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useApi } from '../hooks/useApi';

const DAY_ORDER = ['yesterday', 'today', 'tomorrow', 'day_after_tomorrow'];

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return Number(value).toLocaleString('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const formatSigned = (value, digits = 2, suffix = '') => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  const numeric = Number(value);
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${formatNumber(numeric, digits)}${suffix}`;
};

const numericOrNull = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  return Number(value);
};

const avgDefined = (values) => {
  const filtered = values.map(numericOrNull).filter((value) => value !== null);
  if (!filtered.length) {
    return null;
  }
  return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
};

const PRICE_STACK_SERIES = [
  { key: 'base', label: 'BASE actual', color: '#22d3ee', dashed: false, strokeWidth: 2.4 },
  { key: 'fixIActual', label: 'Fix I actual', color: '#34d399', dashed: false, strokeWidth: 2.2 },
  { key: 'fixIIActual', label: 'Fix II actual', color: '#818cf8', dashed: false, strokeWidth: 2.2 },
  { key: 'ida1Actual', label: 'IDA1 actual', color: '#f472b6', dashed: false, strokeWidth: 2.2 },
  { key: 'ida2Actual', label: 'IDA2 actual', color: '#fbbf24', dashed: false, strokeWidth: 2.2 },
  { key: 'v3Model', label: 'v3.1.0 q50', color: '#86efac', dashed: true, strokeWidth: 1.6 },
  { key: 'v5Model', label: 'v5.0.0 q50', color: '#a5b4fc', dashed: true, strokeWidth: 1.6 },
  { key: 'ida1Model', label: 'IDA1 q50', color: '#f9a8d4', dashed: true, strokeWidth: 1.6 },
  { key: 'ida2Model', label: 'IDA2 q50', color: '#fde68a', dashed: true, strokeWidth: 1.6 },
];

const PRICE_STACK_SERIES_BY_KEY = Object.fromEntries(
  PRICE_STACK_SERIES.map((series) => [series.key, series]),
);

const maxDefinedRow = (rows, accessor) => {
  const filtered = rows.filter((row) => numericOrNull(accessor(row)) !== null);
  if (!filtered.length) {
    return null;
  }
  return filtered.reduce((best, row) => (
    numericOrNull(accessor(row)) > numericOrNull(accessor(best)) ? row : best
  ));
};

const formatDateLabel = (value) => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const blendedSystemValue = (row, key) => {
  const pseKey = `pse_${key}`;
  const meteoKey = `meteo_${key}`;
  return numericOrNull(row?.system?.[pseKey]) ?? numericOrNull(row?.system?.[meteoKey]);
};

const valueTone = (value, darkMode = true) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return darkMode ? 'text-slate-400' : 'text-slate-500';
  }
  if (Number(value) > 0) {
    return darkMode ? 'text-emerald-400' : 'text-emerald-600';
  }
  if (Number(value) < 0) {
    return darkMode ? 'text-rose-400' : 'text-rose-600';
  }
  return darkMode ? 'text-slate-200' : 'text-slate-700';
};

const getShell = (darkMode) => ({
  page: darkMode
    ? 'min-h-screen bg-slate-950 text-slate-100'
    : 'min-h-screen bg-transparent text-slate-900',
  panel: darkMode
    ? 'rounded-2xl border border-slate-800 bg-slate-900/80 shadow-[0_20px_60px_rgba(2,6,23,0.35)]'
    : 'rounded-2xl border border-slate-200 bg-white/95 shadow-[0_16px_40px_rgba(15,23,42,0.08)]',
  inset: darkMode
    ? 'rounded-xl border border-slate-800/80 bg-slate-950/70'
    : 'rounded-xl border border-slate-200 bg-slate-50/90',
  muted: darkMode ? 'text-slate-400' : 'text-slate-600',
  heading: darkMode ? 'text-slate-100' : 'text-slate-900',
  eyebrow: darkMode ? 'text-slate-500' : 'text-slate-500',
  icon: darkMode ? 'text-cyan-300' : 'text-cyan-600',
  softPanel: darkMode
    ? 'rounded-xl border border-slate-800 bg-slate-900/70'
    : 'rounded-xl border border-slate-200 bg-white',
  tableHeader: darkMode
    ? 'sticky top-0 border-b border-slate-800 bg-slate-900 px-3 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-400'
    : 'sticky top-0 border-b border-slate-200 bg-slate-50 px-3 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500',
  tableRow: darkMode ? 'odd:bg-slate-950/40 even:bg-slate-900/35' : 'odd:bg-white even:bg-slate-50/70',
  tableCell: darkMode ? 'border-b border-slate-800 px-3 py-3' : 'border-b border-slate-200 px-3 py-3',
});

const StatTile = ({ eyebrow, value, helper, tone, shell }) => (
  <div className={`${shell.inset} p-4`}>
    <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${shell.eyebrow}`}>{eyebrow}</div>
    <div className={`mt-2 text-2xl font-semibold ${tone}`}>{value}</div>
    {helper ? <div className={`mt-2 text-sm ${shell.muted}`}>{helper}</div> : null}
  </div>
);

const ModelHelperLines = ({ lines, darkMode }) => (
  <div className="space-y-1.5">
    {lines.map((line) => (
      <div
        key={line.label}
        className={`rounded-lg px-2.5 py-1.5 text-sm leading-5 ${
          darkMode
            ? 'bg-slate-800/70 text-slate-200'
            : 'bg-slate-100 text-slate-700'
        }`}
      >
        <span className={`font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{line.label}</span>{' '}
        <span className={darkMode ? 'text-cyan-200' : 'text-cyan-700'}>{line.value}</span>
      </div>
    ))}
  </div>
);

const PriceStackLegend = ({ payload, darkMode }) => {
  const items = (payload || [])
    .map((entry) => PRICE_STACK_SERIES_BY_KEY[entry.dataKey] || null)
    .filter(Boolean);

  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
      {items.map((item) => (
        <div
          key={item.key}
          className={`inline-flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}
        >
          <span
            aria-hidden="true"
            className="inline-block w-4 border-t-2"
            style={{
              borderColor: item.color,
              borderTopStyle: item.dashed ? 'dashed' : 'solid',
              borderTopWidth: `${item.strokeWidth}px`,
            }}
          />
          <span style={{ color: item.color }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

const PriceStackTooltip = ({ active, payload, label, darkMode }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = payload
    .map((entry) => {
      const series = PRICE_STACK_SERIES_BY_KEY[entry.dataKey] || {
        key: entry.dataKey,
        label: entry.name || entry.dataKey,
        color: entry.color || '#94a3b8',
        dashed: false,
        strokeWidth: 2,
      };
      return {
        ...series,
        value: numericOrNull(entry.value),
      };
    })
    .filter((entry) => entry.value !== null)
    .sort((a, b) => b.value - a.value);

  if (!rows.length) {
    return null;
  }

  return (
    <div
      className={`min-w-[220px] rounded-2xl border px-4 py-3 shadow-lg ${
        darkMode
          ? 'border-slate-700 bg-slate-950/95 text-slate-100'
          : 'border-slate-300 bg-white/95 text-slate-900'
      }`}
    >
      <div className="mb-3 text-sm font-semibold">{label}</div>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-4 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block w-4 shrink-0 border-t-2"
                style={{
                  borderColor: row.color,
                  borderTopStyle: row.dashed ? 'dashed' : 'solid',
                  borderTopWidth: `${row.strokeWidth}px`,
                }}
              />
              <span className="truncate" style={{ color: row.color }}>{row.label}</span>
            </div>
            <span className="shrink-0 font-semibold">{formatNumber(row.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, action, shell, darkMode }) => (
  <div className={`flex flex-col gap-3 px-6 py-5 lg:flex-row lg:items-end lg:justify-between ${darkMode ? 'border-b border-slate-800' : 'border-b border-slate-200'}`}>
    <div>
      <div className={`flex items-center gap-2 ${shell.heading}`}>
        <Icon className={`h-4 w-4 ${shell.icon}`} />
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      {subtitle ? <p className={`mt-1 text-sm ${shell.muted}`}>{subtitle}</p> : null}
    </div>
    {action}
  </div>
);

const TransferMap = ({ mapData, shell, darkMode }) => {
  const neighbors = mapData?.neighbors || [];
  const lookup = Object.fromEntries(neighbors.map((item) => [item.code, item.value]));
  const nodeStyle = darkMode
    ? 'rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs font-medium text-slate-200 shadow-sm'
    : 'rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm';

  return (
    <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
      <div className={`${shell.inset} p-4`}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className={`text-xs font-semibold uppercase tracking-[0.14em] ${shell.eyebrow}`}>PL / region</div>
            <div className={`mt-1 text-sm ${shell.muted}`}>Lagged net exchanges vs Poland plus total import/export capacity.</div>
          </div>
          <div className={`text-right text-xs ${shell.muted}`}>
            <div>Export {formatNumber(mapData?.total_export_capacity, 0)} MW</div>
            <div>Import {formatNumber(mapData?.total_import_capacity, 0)} MW</div>
          </div>
        </div>
        <div className={`relative mx-auto h-[300px] max-w-[520px] overflow-hidden rounded-xl ${darkMode ? 'border border-slate-800 bg-[radial-gradient(circle_at_center,rgba(15,23,42,0.98),rgba(2,6,23,1))]' : 'border border-slate-200 bg-[radial-gradient(circle_at_center,rgba(248,250,252,0.98),rgba(241,245,249,1))]'}`}>
          <svg viewBox="0 0 520 300" className="absolute inset-0 h-full w-full">
            {[
              { code: 'DE', x1: 240, y1: 152, x2: 124, y2: 150 },
              { code: 'CZ', x1: 250, y1: 166, x2: 174, y2: 218 },
              { code: 'SK', x1: 272, y1: 170, x2: 316, y2: 220 },
              { code: 'LT', x1: 282, y1: 135, x2: 360, y2: 86 },
              { code: 'SE4', x1: 260, y1: 126, x2: 210, y2: 58 },
            ].map((edge) => (
              <g key={edge.code}>
                <line
                  x1={edge.x1}
                  y1={edge.y1}
                  x2={edge.x2}
                  y2={edge.y2}
                  stroke={lookup[edge.code] !== null && lookup[edge.code] !== undefined ? '#38bdf8' : (darkMode ? '#334155' : '#94a3b8')}
                  strokeWidth="2.5"
                  strokeDasharray={lookup[edge.code] !== null && lookup[edge.code] !== undefined ? '0' : '5 5'}
                  opacity="0.85"
                />
              </g>
            ))}
            <circle cx="258" cy="148" r="34" fill="rgba(34,197,94,0.16)" stroke="#22c55e" strokeWidth="2.5" />
            <text x="258" y="145" textAnchor="middle" fill={darkMode ? '#f8fafc' : '#0f172a'} fontSize="18" fontWeight="700">PL</text>
            <text x="258" y="164" textAnchor="middle" fill={darkMode ? '#cbd5e1' : '#475569'} fontSize="11">hub</text>
          </svg>

          <div className="absolute left-[72px] top-[132px]">
            <div className={nodeStyle}>DE {formatSigned(lookup.DE, 0)}</div>
          </div>
          <div className="absolute left-[164px] top-[212px]">
            <div className={nodeStyle}>CZ {formatSigned(lookup.CZ, 0)}</div>
          </div>
          <div className="absolute left-[308px] top-[214px]">
            <div className={nodeStyle}>SK {formatSigned(lookup.SK, 0)}</div>
          </div>
          <div className="absolute left-[348px] top-[60px]">
            <div className={nodeStyle}>LT {formatSigned(lookup.LT, 0)}</div>
          </div>
          <div className="absolute left-[172px] top-[32px]">
            <div className={nodeStyle}>SE4 {formatSigned(lookup.SE4, 0)}</div>
          </div>
        </div>
      </div>

      <div className={`${shell.inset} p-4`}>
        <div className="grid gap-3 sm:grid-cols-2">
          {neighbors.map((item) => (
            <div key={item.code} className={`${shell.softPanel} p-4`}>
              <div className={`text-xs font-semibold uppercase tracking-[0.14em] ${shell.eyebrow}`}>{item.code}</div>
              <div className={`mt-2 text-2xl font-semibold ${valueTone(item.value, darkMode)}`}>{formatSigned(item.value, 0)}</div>
              <div className={`mt-1 text-xs ${shell.muted}`}>lag24 exchange vs PL</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Dashboard = ({ darkMode = false }) => {
  const { fetchGet } = useApi();
  const shell = getShell(darkMode);
  const [dashboard, setDashboard] = useState(null);
  const [selectedView, setSelectedView] = useState('tomorrow');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const response = await fetchGet('/dashboard');
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to load dashboard');
      }
      setDashboard(data);
      setSelectedView((current) => (current ? current : data.default_view || 'tomorrow'));
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchGet]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadDashboard({ silent: true });
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [loadDashboard]);

  const activeView = dashboard?.views?.[selectedView] || null;
  const dayTabs = useMemo(() => (
    DAY_ORDER.map((key) => ({
      key,
      label: dashboard?.views?.[key]?.label || key,
      date: dashboard?.views?.[key]?.date || '',
    }))
  ), [dashboard]);

  const priceChartData = useMemo(() => (
    (activeView?.hourly || []).map((row) => ({
      hour: row.hour_label,
      base: row.actual.base,
      fixIActual: row.actual.fix_i,
      fixIIActual: row.actual.fix_ii,
      ida1Actual: row.actual.ida1,
      ida2Actual: row.actual.ida2,
      v3Model: row.model.v3_q50,
      v5Model: row.model.v5_q50,
      ida1Model: row.model.ida1_q50,
      ida2Model: row.model.ida2_q50,
    }))
  ), [activeView]);

  const renewablesChartData = useMemo(() => (
    (activeView?.hourly || []).map((row) => ({
      hour: row.hour_label,
      wind: blendedSystemValue(row, 'wind'),
      pv: blendedSystemValue(row, 'pv'),
      load: blendedSystemValue(row, 'load'),
      zrb: row.system.pse_zrb,
      importCap: row.cross_border.total_import_capacity,
      exportCap: row.cross_border.total_export_capacity,
    }))
  ), [activeView]);

  const blendedSystemSummary = useMemo(() => {
    const hourly = activeView?.hourly || [];
    const peakWindRow = maxDefinedRow(hourly, (row) => blendedSystemValue(row, 'wind'));
    const peakPvRow = maxDefinedRow(hourly, (row) => blendedSystemValue(row, 'pv'));

    return {
      avgWind: avgDefined(hourly.map((row) => blendedSystemValue(row, 'wind'))),
      avgPv: avgDefined(hourly.map((row) => blendedSystemValue(row, 'pv'))),
      avgLoad: avgDefined(hourly.map((row) => blendedSystemValue(row, 'load'))),
      peakWindHour: peakWindRow?.hour_label || null,
      peakPvHour: peakPvRow?.hour_label || null,
    };
  }, [activeView]);

  const gasSummary = dashboard?.gas || null;
  const gasChartData = useMemo(() => (
    (gasSummary?.series || []).map((row) => ({
      date: row.date,
      dateLabel: formatDateLabel(row.date),
      price: numericOrNull(row.price),
    }))
  ), [gasSummary]);

  const pageClassName = shell.page;

  if (loading) {
    return (
      <div className={pageClassName}>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className={`flex items-center gap-3 ${shell.muted}`}>
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span>Loading trading dashboard…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageClassName}>
      <div className="mx-auto max-w-[1600px] px-6 py-8">
        <section className={`mb-8 overflow-hidden rounded-[28px] ${darkMode ? 'border border-slate-800 bg-[linear-gradient(135deg,rgba(15,23,42,0.98),rgba(8,47,73,0.88))] shadow-[0_24px_80px_rgba(2,6,23,0.45)]' : 'border border-slate-200 bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(236,253,255,0.96))] shadow-[0_18px_50px_rgba(15,23,42,0.08)]'}`}>
          <div className="grid gap-8 px-8 py-8 xl:grid-cols-[1.2fr_0.8fr]">
            <div>
              <div className={`mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${darkMode ? 'border border-cyan-400/30 bg-cyan-400/10 text-cyan-200' : 'border border-cyan-300 bg-cyan-50 text-cyan-700'}`}>
                <Gauge className="h-3.5 w-3.5" />
                Trader Dashboard
              </div>
              <h1 className={`max-w-3xl text-4xl font-semibold tracking-tight ${darkMode ? 'text-slate-50' : 'text-slate-900'}`}>
                One screen for reading the day fast: TGE, models, weather, exports and midday risk.
              </h1>
              <p className={`mt-4 max-w-3xl text-base leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                The dashboard brings together actual auction results, live Fix I / Fix II / IDA1 / IDA2 model predictions,
                weather signals and cross-border context, so a long or kill decision does not require jumping across multiple tabs.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                {dayTabs.map((tab) => {
                  const isActive = selectedView === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setSelectedView(tab.key)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                        isActive
                          ? (darkMode ? 'border-cyan-300 bg-cyan-300 text-slate-950 shadow-[0_8px_30px_rgba(103,232,249,0.25)]' : 'border-cyan-300 bg-cyan-300 text-slate-950 shadow-[0_8px_24px_rgba(34,211,238,0.22)]')
                          : (darkMode ? 'border-slate-700 bg-slate-950/60 text-slate-300 hover:border-slate-500 hover:text-slate-100' : 'border-slate-300 bg-white/90 text-slate-700 hover:border-slate-400 hover:text-slate-900')
                      }`}
                    >
                      <span>{tab.label}</span>
                      <span className={`ml-2 text-xs ${isActive ? (darkMode ? 'text-slate-900/75' : 'text-slate-900/70') : 'text-slate-500'}`}>{tab.date}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4 self-start">
              <div className={`${shell.panel} p-5`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${shell.eyebrow}`}>Refresh</div>
                    <div className={`mt-2 text-xl font-semibold ${shell.heading}`}>
                      {refreshing ? 'Sync in progress' : 'Auto-refresh every 60 s'}
                    </div>
                    <div className={`mt-1 text-sm ${shell.muted}`}>
                      Last payload: {dashboard?.generated_at ? new Date(dashboard.generated_at).toLocaleString('en-GB') : '—'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadDashboard({ silent: true })}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium transition ${darkMode ? 'border border-slate-700 bg-slate-950/80 text-slate-100 hover:border-slate-500 hover:bg-slate-900' : 'border border-slate-300 bg-white text-slate-900 hover:border-slate-400 hover:bg-slate-50'}`}
                  >
                    <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              <div className={`${shell.panel} grid gap-3 p-5 sm:grid-cols-2`}>
                <StatTile
                  eyebrow="Models"
                  value={`${dashboard?.models?.v3 || '—'} / ${dashboard?.models?.v5 || '—'}`}
                  helper={`Fix II models plus ${dashboard?.models?.ida1 || '—'} and ${dashboard?.models?.ida2 || '—'} for IDA1 and IDA2.`}
                  tone={shell.heading}
                  shell={shell}
                />
                <StatTile
                  eyebrow="Active View"
                  value={activeView?.date || '—'}
                  helper={`${activeView?.label || ''} trading view`}
                  tone={shell.heading}
                  shell={shell}
                />
              </div>
            </div>
          </div>
        </section>

        {error ? (
          <div className={`mb-6 rounded-2xl px-5 py-4 ${darkMode ? 'border border-rose-500/40 bg-rose-500/10 text-rose-200' : 'border border-rose-300 bg-rose-50 text-rose-700'}`}>
            {error}
          </div>
        ) : null}

        {activeView ? (
          <>
            <section className={`${shell.panel} mb-8`}>
              <SectionHeader
                icon={ArrowDownUp}
                title="Auctions and Models"
                subtitle="A quick comparison of what the market has already done versus what the models show for the selected day."
                shell={shell}
                darkMode={darkMode}
              />
              <div className="grid gap-4 px-6 py-6 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <StatTile eyebrow="BASE" value={`${formatNumber(activeView.summary.actual_avg.base)} PLN`} helper="Daily base price" tone={shell.heading} shell={shell} />
                  <StatTile eyebrow="Fix I actual" value={`${formatNumber(activeView.summary.actual_avg.fix_i)} PLN`} helper="Observed auction result." tone={shell.heading} shell={shell} />
                  <StatTile
                    eyebrow="Fix II actual"
                    value={`${formatNumber(activeView.summary.actual_avg.fix_ii)} PLN`}
                    helper={(
                      <ModelHelperLines
                        darkMode={darkMode}
                        lines={[
                          { label: 'v3.1.0 q50', value: `${formatNumber(activeView.summary.model_avg.v3_q50)} PLN` },
                          { label: 'v5.0.0 q50', value: `${formatNumber(activeView.summary.model_avg.v5_q50)} PLN` },
                        ]}
                      />
                    )}
                    tone={shell.heading}
                    shell={shell}
                  />
                  <StatTile
                    eyebrow="IDA1 actual"
                    value={`${formatNumber(activeView.summary.actual_avg.ida1)} PLN`}
                    helper={(
                      <ModelHelperLines
                        darkMode={darkMode}
                        lines={[
                          { label: 'ida1-v1.0.3 q50', value: `${formatNumber(activeView.summary.model_avg.ida1_q50)} PLN` },
                        ]}
                      />
                    )}
                    tone={shell.heading}
                    shell={shell}
                  />
                  <StatTile
                    eyebrow="IDA2 actual"
                    value={`${formatNumber(activeView.summary.actual_avg.ida2)} PLN`}
                    helper={(
                      <ModelHelperLines
                        darkMode={darkMode}
                        lines={[
                          { label: 'ida2-v1.0.0 q50', value: `${formatNumber(activeView.summary.model_avg.ida2_q50)} PLN` },
                        ]}
                      />
                    )}
                    tone={shell.heading}
                    shell={shell}
                  />
                  <StatTile
                    eyebrow="DE avg"
                    value={`${formatNumber(activeView.meteo_de.avg_eur)} EUR`}
                    helper={activeView.meteo_de.avg_pln !== null ? `${formatNumber(activeView.meteo_de.avg_pln)} PLN @ ${formatNumber(activeView.meteo_de.eur_pln_rate, 4)}` : 'Missing FX rate / forecast'}
                    tone={shell.heading}
                    shell={shell}
                  />
                </div>

                <div className={`${shell.inset} p-4`}>
                  <div className={`mb-4 flex items-center gap-2 ${shell.heading}`}>
                    <Activity className={`h-4 w-4 ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`} />
                    <h3 className={`text-sm font-semibold uppercase tracking-[0.14em] ${shell.muted}`}>Trader context</h3>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className={`${shell.softPanel} p-4`}>
                      <div className={`text-xs uppercase tracking-[0.14em] ${shell.eyebrow}`}>DE minus PL meteo</div>
                      <div className={`mt-2 text-2xl font-semibold ${valueTone(activeView.summary.system_avg.de_minus_pl, darkMode)}`}>
                        {formatSigned(activeView.summary.system_avg.de_minus_pl, 2, ' PLN')}
                      </div>
                      <div className={`mt-1 text-xs ${shell.muted}`}>average daily spread</div>
                    </div>
                    <div className={`${shell.softPanel} p-4`}>
                      <div className={`text-xs uppercase tracking-[0.14em] ${shell.eyebrow}`}>Lowest midday ZRB</div>
                      <div className={`mt-2 text-2xl font-semibold ${valueTone(activeView.highlights.lowest_midday_zrb.value, darkMode)}`}>
                        {formatSigned(activeView.highlights.lowest_midday_zrb.value, 0, ' MW')}
                      </div>
                      <div className={`mt-1 text-xs ${shell.muted}`}>{activeView.highlights.lowest_midday_zrb.hour_label || 'No data'}</div>
                    </div>
                    <div className={`${shell.softPanel} p-4`}>
                      <div className={`text-xs uppercase tracking-[0.14em] ${shell.eyebrow}`}>Peak wind</div>
                      <div className={`mt-2 text-2xl font-semibold ${shell.heading}`}>{formatNumber(activeView.highlights.peak_wind.value, 0)} MW</div>
                      <div className={`mt-1 text-xs ${shell.muted}`}>{activeView.highlights.peak_wind.hour_label || 'No data'}</div>
                    </div>
                    <div className={`${shell.softPanel} p-4`}>
                      <div className={`text-xs uppercase tracking-[0.14em] ${shell.eyebrow}`}>Peak export cap</div>
                      <div className={`mt-2 text-2xl font-semibold ${shell.heading}`}>{formatNumber(activeView.highlights.peak_export_capacity.value, 0)} MW</div>
                      <div className={`mt-1 text-xs ${shell.muted}`}>{activeView.highlights.peak_export_capacity.hour_label || 'No data'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className={`${shell.panel} mb-8`}>
              <SectionHeader
                icon={Bot}
                title="Price stack"
                subtitle="Actual auction levels versus model outputs on the same hourly axis."
                shell={shell}
                darkMode={darkMode}
              />
              <div className="px-6 py-5">
                <div className="h-[420px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={priceChartData} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={darkMode ? '#1e293b' : '#cbd5e1'} strokeDasharray="3 3" />
                      <XAxis dataKey="hour" stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }} />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }} />
                      <Tooltip
                        content={<PriceStackTooltip darkMode={darkMode} />}
                      />
                      <Legend content={<PriceStackLegend darkMode={darkMode} />} />
                      <ReferenceLine y={0} stroke={darkMode ? '#475569' : '#94a3b8'} strokeDasharray="4 4" />
                      {PRICE_STACK_SERIES.map((series) => (
                        <Line
                          key={series.key}
                          type="monotone"
                          dataKey={series.key}
                          stroke={series.color}
                          strokeWidth={series.strokeWidth}
                          strokeDasharray={series.dashed ? '4 4' : undefined}
                          dot={false}
                          name={series.label}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            <section className={`${shell.panel} mb-8`}>
              <SectionHeader
                icon={Wind}
                title="Weather, ZRB and Capacity Context"
                subtitle="This section helps judge whether midday is primarily a wind, PV or export-driven case. Wind, PV and load prefer PSE when available and fall back to Meteologica."
                shell={shell}
                darkMode={darkMode}
              />
              <div className="grid gap-6 px-6 py-6 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="h-[380px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={renewablesChartData} margin={{ top: 10, right: 18, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={darkMode ? '#1e293b' : '#cbd5e1'} strokeDasharray="3 3" />
                      <XAxis dataKey="hour" stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }} />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: darkMode ? '#020617' : '#ffffff',
                          borderColor: darkMode ? '#334155' : '#cbd5e1',
                          borderRadius: '14px',
                          color: darkMode ? '#f8fafc' : '#0f172a',
                        }}
                      />
                      <Legend />
                      <Bar dataKey="wind" fill="#38bdf8" name="Wind forecast" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pv" fill="#f59e0b" name="PV forecast" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="load" stroke="#e2e8f0" strokeWidth={2.2} dot={false} name="Load forecast" />
                      <Line type="monotone" dataKey="zrb" stroke="#34d399" strokeWidth={2.2} dot={false} name="PSE ZRB" />
                      <Line type="monotone" dataKey="importCap" stroke="#22c55e" strokeWidth={2.2} dot={false} strokeDasharray="6 5" name="Total import cap" />
                      <Line type="monotone" dataKey="exportCap" stroke="#a78bfa" strokeWidth={2.2} dot={false} strokeDasharray="5 4" name="Total export cap" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid gap-4">
                  <StatTile eyebrow="Wind forecast avg" value={`${formatNumber(blendedSystemSummary.avgWind, 0)} MW`} helper={`Peak ${blendedSystemSummary.peakWindHour || '—'} • PSE preferred, Meteo fallback`} tone={shell.heading} shell={shell} />
                  <StatTile eyebrow="PV forecast avg" value={`${formatNumber(blendedSystemSummary.avgPv, 0)} MW`} helper={`Peak ${blendedSystemSummary.peakPvHour || '—'} • PSE preferred, Meteo fallback`} tone={shell.heading} shell={shell} />
                  <StatTile eyebrow="Load forecast avg" value={`${formatNumber(blendedSystemSummary.avgLoad, 0)} MW`} helper={`ZRB avg ${formatSigned(activeView.summary.system_avg.pse_zrb, 0, ' MW')}`} tone={shell.heading} shell={shell} />
                  <StatTile
                    eyebrow="TTF gas DB series"
                    value={gasSummary?.latest_value !== null && gasSummary?.latest_value !== undefined ? `${formatNumber(gasSummary.latest_value)} EUR/MWh` : '—'}
                    helper={
                      gasSummary?.latest_date
                        ? `${gasSummary.status === 'stale' ? `Stored only • ${gasSummary.stale_days}d stale` : 'Fresh DB point'} • calendar date ${gasSummary.latest_date}`
                        : 'No stored TTF series'
                    }
                    tone={gasSummary?.status === 'stale' ? shell.muted : shell.heading}
                    shell={shell}
                  />
                </div>
              </div>
            </section>

            <section className={`${shell.panel} mb-8`}>
              <SectionHeader
                icon={Flame}
                title="Gas context"
                subtitle="Stored TTF gas futures context from the DB. Non-trading days are forward-filled by the loader so the dashboard keeps a current calendar-day value."
                shell={shell}
                darkMode={darkMode}
              />
              <div className="grid gap-6 px-6 py-6 xl:grid-cols-[1.1fr_0.9fr]">
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={gasChartData} margin={{ top: 10, right: 18, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={darkMode ? '#1e293b' : '#cbd5e1'} strokeDasharray="3 3" />
                      <XAxis dataKey="dateLabel" stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }} minTickGap={20} />
                      <YAxis stroke={darkMode ? '#94a3b8' : '#64748b'} tick={{ fill: darkMode ? '#94a3b8' : '#64748b', fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => `${formatNumber(value)} EUR/MWh`}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.date || '—'}
                        contentStyle={{
                          backgroundColor: darkMode ? '#020617' : '#ffffff',
                          borderColor: darkMode ? '#334155' : '#cbd5e1',
                          borderRadius: '14px',
                          color: darkMode ? '#f8fafc' : '#0f172a',
                        }}
                      />
                      <Line type="monotone" dataKey="price" stroke="#fb923c" strokeWidth={2.4} dot={false} name="TTF gas" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid gap-4">
                  <StatTile
                    eyebrow="Current TTF calendar value"
                    value={gasSummary?.latest_value !== null && gasSummary?.latest_value !== undefined ? `${formatNumber(gasSummary.latest_value)} EUR/MWh` : '—'}
                    helper={
                      gasSummary?.latest_date
                        ? `Calendar date ${gasSummary.latest_date}${gasSummary.latest_issue_date ? ` • issue ${gasSummary.latest_issue_date}` : ''}`
                        : 'No stored value'
                    }
                    tone={shell.heading}
                    shell={shell}
                  />
                  <StatTile
                    eyebrow="Freshness"
                    value={
                      gasSummary?.status === 'stale'
                        ? `${formatNumber(gasSummary?.stale_days, 0)} days stale`
                        : (gasSummary?.status === 'live' ? 'Fresh in DB' : 'Unavailable')
                    }
                    helper={
                      gasSummary?.status === 'live'
                        ? 'Calendar point is current for today'
                        : (gasSummary?.latest_issue_date ? `Latest issue ${gasSummary.latest_issue_date}` : 'No issue date')
                    }
                    tone={gasSummary?.status === 'stale' ? shell.muted : shell.heading}
                    shell={shell}
                  />
                  <StatTile
                    eyebrow="Source"
                    value={gasSummary?.source || '—'}
                    helper="Stored DB values only. Non-trading days carry forward the latest available Yahoo/TTF close."
                    tone={shell.heading}
                    shell={shell}
                  />
                </div>
              </div>
            </section>

            <section className={`${shell.panel} mb-8`}>
              <SectionHeader
                icon={Map}
                title="Cross-border map"
                subtitle="Lagged exchanges plus total import / export capacity for PL."
                shell={shell}
                darkMode={darkMode}
              />
              <div className="px-6 py-6">
                <TransferMap mapData={activeView.map} shell={shell} darkMode={darkMode} />
              </div>
            </section>

            <section className={`${shell.panel}`}>
              <SectionHeader
                icon={CalendarDays}
                title="Hourly sheet"
                subtitle="Hard numbers for manual checking: auctions, models, weather, DE and capacity in one grid."
                shell={shell}
                darkMode={darkMode}
                action={(
                  <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${darkMode ? 'border border-slate-700 bg-slate-950/70 text-slate-300' : 'border border-slate-300 bg-white text-slate-600'}`}>
                    <SunMedium className={`h-3.5 w-3.5 ${darkMode ? 'text-amber-300' : 'text-amber-500'}`} />
                    24 rows
                  </div>
                )}
              />
              <div className="overflow-x-auto px-6 py-6">
                <table className="min-w-[1440px] border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      {[
                        'Hour',
                        'Base',
                        'Fix I',
                        'Fix II',
                        'IDA1',
                        'IDA2',
                        'v3.1.0 q50',
                        'v5.0.0 q50',
                        'IDA1 q50',
                        'IDA2 q50',
                        'PSE wind',
                        'PSE PV',
                        'PSE load',
                        'ZRB',
                        'DE-PL',
                        'Import cap',
                        'Export cap',
                      ].map((label) => (
                        <th key={label} className={shell.tableHeader}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(activeView.hourly || []).map((row) => (
                      <tr key={row.hour_label} className={shell.tableRow}>
                        <td className={`${shell.tableCell} font-medium ${shell.heading}`}>{row.hour_label}</td>
                        <td className={shell.tableCell}>{formatNumber(row.actual.base)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.actual.fix_i)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.actual.fix_ii)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.actual.ida1)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.actual.ida2)}</td>
                        <td className={`${shell.tableCell} ${darkMode ? 'text-cyan-200' : 'text-cyan-700'}`}>{formatNumber(row.model.v3_q50)}</td>
                        <td className={`${shell.tableCell} ${darkMode ? 'text-cyan-200' : 'text-cyan-700'}`}>{formatNumber(row.model.v5_q50)}</td>
                        <td className={`${shell.tableCell} ${darkMode ? 'text-fuchsia-200' : 'text-fuchsia-700'}`}>{formatNumber(row.model.ida1_q50)}</td>
                        <td className={`${shell.tableCell} ${darkMode ? 'text-amber-200' : 'text-amber-700'}`}>{formatNumber(row.model.ida2_q50)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.system.pse_wind, 0)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.system.pse_pv, 0)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.system.pse_load, 0)}</td>
                        <td className={`${shell.tableCell} ${valueTone(row.system.pse_zrb, darkMode)}`}>{formatSigned(row.system.pse_zrb, 0)}</td>
                        <td className={`${shell.tableCell} ${valueTone(row.system.de_minus_pl, darkMode)}`}>{formatSigned(row.system.de_minus_pl)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.cross_border.total_import_capacity, 0)}</td>
                        <td className={shell.tableCell}>{formatNumber(row.cross_border.total_export_capacity, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Dashboard;
